import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Feriado {
  id: string;
  nome: string;
  data: string;
  tipo: string;
  municipio_id: string | null;
  recorrente: boolean;
  observacoes: string | null;
  criado_em: string;
}

interface MunicipioOption {
  id: string;
  nome: string;
  sigla: string | null;
}

export default function FeriadosTab() {
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [editFeriado, setEditFeriado] = useState<Feriado | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [filtroAno, setFiltroAno] = useState<string>('2025');
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: feriadosData, error: feriadosErr }, { data: munData, error: munErr }] = await Promise.all([
      supabase.from('feriados').select('*').order('data'),
      supabase.from('municipios').select('id, nome, sigla').eq('ativo', true).order('nome'),
    ]);
    if (feriadosErr) setError('Erro ao carregar feriados: ' + feriadosErr.message);
    if (munErr) setError('Erro ao carregar municípios: ' + munErr.message);
    setFeriados(feriadosData ?? []);
    setMunicipios(munData ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editFeriado) return;
    setError('');
    setMsg('');

    const formData = new FormData(e.currentTarget);
    const data = {
      nome: String(formData.get('nome') ?? '').trim(),
      data: String(formData.get('data') ?? '').trim(),
      tipo: String(formData.get('tipo') ?? '').trim(),
      municipio_id: String(formData.get('municipio_id') ?? '').trim() || null,
      recorrente: formData.get('recorrente') === 'on',
      observacoes: String(formData.get('observacoes') ?? '').trim() || null,
    };

    if (!data.nome || !data.data) {
      setError('Nome e data são obrigatórios.');
      return;
    }

    const { error: err } = editFeriado.id
      ? await supabase.from('feriados').update(data).eq('id', editFeriado.id)
      : await supabase.from('feriados').insert(data);

    if (err) {
      setError('Erro ao guardar: ' + err.message);
    } else {
      setMsg(editFeriado.id ? 'Feriado atualizado!' : 'Feriado criado!');
      setEditFeriado(null);
      setShowAdd(false);
      await fetchData();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleDelete = async (f: Feriado) => {
    if (!confirm(`Eliminar feriado "${f.nome}"?`)) return;
    setError('');
    const { error: err } = await supabase.from('feriados').delete().eq('id', f.id);
    if (err) {
      setError('Erro ao eliminar: ' + err.message);
    } else {
      setMsg('Feriado eliminado.');
      await fetchData();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const anos = Array.from(new Set(feriados.map((f) => f.data.slice(0, 4)))).sort();

  const feriadosFiltrados = feriados.filter((f) => {
    const anoMatch = filtroAno === 'todos' || f.data.startsWith(filtroAno);
    const tipoMatch = filtroTipo === 'todos' || f.tipo === filtroTipo;
    return anoMatch && tipoMatch;
  });

  const tipoBadge: Record<string, string> = {
    nacional: 'bg-amber-50 text-amber-700 border border-amber-200',
    municipal: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    outro: 'bg-zinc-100 text-zinc-600 border border-zinc-200',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-xl flex-shrink-0">
              <i className="ri-calendar-event-line text-white text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-zinc-800">Gestão de Feriados</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Feriados nacionais e municipais para cálculo de prazos e calendário</p>
            </div>
          </div>
          <button
            onClick={() => { setEditFeriado({ id: '', nome: '', data: '', tipo: 'nacional', municipio_id: null, recorrente: true, observacoes: '', criado_em: '' }); setShowAdd(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Novo Feriado
          </button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-emerald-500 text-sm" /></div>
          <p className="text-[13px] text-emerald-700">{msg}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-red-500 text-sm" /></div>
          <p className="text-[13px] text-red-700">{error}</p>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-filter-3-line text-sm" /></div>
          <span className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wider">Filtrar</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={filtroAno}
            onChange={(e) => setFiltroAno(e.target.value)}
            className="px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-700 focus:outline-none focus:border-zinc-400 cursor-pointer"
          >
            <option value="todos">Todos os anos</option>
            {anos.map((a) => (<option key={a} value={a}>{a}</option>))}
          </select>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-700 focus:outline-none focus:border-zinc-400 cursor-pointer"
          >
            <option value="todos">Todos os tipos</option>
            <option value="nacional">Nacional</option>
            <option value="municipal">Municipal</option>
            <option value="outro">Outro</option>
          </select>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : feriadosFiltrados.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
              <i className="ri-calendar-line text-zinc-400 text-lg" />
            </div>
            <p className="text-[13px] text-zinc-400">Nenhum feriado encontrado</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {feriadosFiltrados.map((f) => {
              const mun = municipios.find((m) => m.id === f.municipio_id);
              return (
                <div key={f.id} className="px-6 py-4 hover:bg-zinc-50/60 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0 mt-0.5">
                        <i className="ri-calendar-event-line text-zinc-500 text-lg" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[13px] font-semibold text-zinc-800">{f.nome}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tipoBadge[f.tipo] ?? tipoBadge.outro}`}>{f.tipo}</span>
                          {f.recorrente && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">Recorrente</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-[12px] text-zinc-600 font-medium">
                            <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calendar-check-line text-xs" /></div>
                            {new Date(f.data).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                          {mun && (
                            <span className="text-[11px] text-zinc-400">Município: {mun.nome}</span>
                          )}
                        </div>
                        {f.observacoes && (
                          <p className="text-[11px] text-zinc-400 mt-1">{f.observacoes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setEditFeriado(f); setShowAdd(true); }}
                        className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(f)}
                        className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {showAdd && editFeriado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">{editFeriado.id ? 'Editar Feriado' : 'Novo Feriado'}</h3>
              <button onClick={() => { setShowAdd(false); setEditFeriado(null); }} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Nome</label>
                <input name="nome" defaultValue={editFeriado.nome} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Ex: Dia da Cidade" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Data</label>
                  <input name="data" type="date" defaultValue={editFeriado.data} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Tipo</label>
                  <select name="tipo" defaultValue={editFeriado.tipo} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors cursor-pointer">
                    <option value="nacional">Nacional</option>
                    <option value="municipal">Municipal</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Município (apenas para feriados municipais)</label>
                <select name="municipio_id" defaultValue={editFeriado.municipio_id ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors cursor-pointer">
                  <option value="">— Nenhum —</option>
                  {municipios.map((m) => (
                    <option key={m.id} value={m.id}>{m.nome}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  name="recorrente"
                  type="checkbox"
                  defaultChecked={editFeriado.recorrente}
                  id="recorrente"
                  className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 cursor-pointer"
                />
                <label htmlFor="recorrente" className="text-[13px] text-zinc-700 cursor-pointer">Feriado recorrente (repete todos os anos)</label>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Observações</label>
                <input name="observacoes" defaultValue={editFeriado.observacoes ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Observações opcionais" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowAdd(false); setEditFeriado(null); }} className="px-4 py-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-800 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">
                  {editFeriado.id ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}